import { TiledMap, parseMap, type TiledMapData, type TiledTileset, type TiledTilesetRef } from "pixi-tiledmap";
import { Texture } from "pixi.js";
import type { AssetLoader } from "../assets/AssetLoader";
import { parseTerrainGridFromTiledMap, type ParsedTerrainGrid, type ParsedTerrainObject } from "../world/terrain";

export interface LoadedTerrain {
  readonly map: TiledMap;
  readonly grid: ParsedTerrainGrid;
  readonly objects: readonly ParsedTerrainObject[];
}

export async function loadTerrainFromTiledFile(
  terrainFile: string,
  assets: AssetLoader,
): Promise<LoadedTerrain> {
  const mapUrl = new URL(`/worlds/${terrainFile}`, globalThis.location.origin);
  const mapData = await fetchJson<TiledMapData>(mapUrl);
  const tilesetRef = mapData.tilesets[0];
  if (!tilesetRef) throw new Error(`Terrain map "${terrainFile}" has no tilesets`);

  const { source, tileset } = await resolveTileset(mapUrl, tilesetRef);
  const externalTilesets = new Map<string, TiledTileset>();
  if (source) externalTilesets.set(source, tileset);

  const image = tileset.image;
  if (!image) throw new Error(`Terrain tileset for "${terrainFile}" missing image`);
  const imageUrl = new URL(image, source ? new URL(source, mapUrl) : mapUrl);
  const tilesetTexture = await assets.loadExternalTexture(image, imageUrl.pathname);
  const tilesetTextures = new Map<string, Texture>();
  tilesetTextures.set(image, tilesetTexture);

  const grid = parseTerrainGridFromTiledMap(mapData, tileset);

  const renderableMapData = { ...mapData, layers: stripObjectLayers(mapData.layers) };
  const resolvedMap = parseMap(renderableMapData, { externalTilesets });
  const map = new TiledMap(resolvedMap, { tilesetTextures });
  return { map, grid, objects: grid.objects };
}

async function resolveTileset(
  mapUrl: URL,
  tilesetRef: TiledMapData["tilesets"][number],
): Promise<{ source?: string; tileset: TiledTileset }> {
  if (isTilesetRef(tilesetRef)) {
    const tilesetUrl = new URL(tilesetRef.source, mapUrl);
    return { source: tilesetRef.source, tileset: await fetchJson<TiledTileset>(tilesetUrl) };
  }
  return { tileset: tilesetRef };
}

function isTilesetRef(tileset: TiledMapData["tilesets"][number]): tileset is TiledTilesetRef {
  return "source" in tileset && !("name" in tileset);
}

async function fetchJson<T>(url: URL): Promise<T> {
  const response = await fetch(url.pathname);
  if (!response.ok) throw new Error(`Failed to fetch ${url.pathname}: ${response.status}`);
  return response.json() as Promise<T>;
}

function stripObjectLayers(layers: TiledMapData["layers"]): TiledMapData["layers"] {
  return layers
    .filter((layer) => layer.type !== "objectgroup")
    .map((layer) =>
      layer.type === "group" && layer.layers
        ? { ...layer, layers: stripObjectLayers(layer.layers) }
        : layer,
    );
}
